import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Headphones, Send, Loader2, Wifi, CheckCircle, MessageSquarePlus, Minus, XCircle } from 'lucide-react';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';

interface LiveSupportChatProps {
  onClose: () => void;
  onStartNewChat?: () => void;
}

const LiveSupportChat: React.FC<LiveSupportChatProps> = ({ onClose, onStartNewChat }) => {
  const { isLoggedIn } = useSarLabAuth();
  const {
    activeTicket,
    messages,
    isConnected,
    isLoadingTicket,
    typingUser,
    createTicket,
    sendSupportMessage,
    sendTyping,
    stopTyping,
    closeTicket,
  } = useLiveSupport();

  const isTicketClosed = activeTicket?.status === 'closed';

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);

    if (!isTyping && e.target.value.trim()) {
      setIsTyping(true);
      sendTyping();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      stopTyping();
    }, 2000);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !isLoggedIn) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setIsTyping(false);
    stopTyping();

    setSending(true);
    try {
      if (!activeTicket) {
        await createTicket('other', messageContent);
      } else {
        await sendSupportMessage(messageContent);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleStartNewChat = async () => {
    // Close the current ticket and clear state so user can start fresh
    await closeTicket();
    // Notify parent to switch to support form view
    if (onStartNewChat) {
      onStartNewChat();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
            <Headphones className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-sm font-medium text-white">Canli Destek</span>
            <div className="flex items-center space-x-1 text-xs text-gray-400">
              {isConnected ? (
                <>
                  <Wifi className="w-3 h-3 text-green-500" />
                  <span className="text-green-400">Bagli</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span>Cevrimici</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={onClose}
            className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
            title="Kucult"
          >
            <Minus className="w-4 h-4 text-gray-400 hover:text-white" />
          </button>
          {activeTicket && activeTicket.status !== 'closed' && !showEndConfirm && (
            <button
              onClick={() => setShowEndConfirm(true)}
              className="p-1 hover:bg-red-500/20 rounded transition-colors duration-200"
              title="Gorusmeyi Sonlandir"
            >
              <XCircle className="w-4 h-4 text-gray-400 hover:text-red-400" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {!isLoggedIn ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Headphones className="w-12 h-12 text-gray-500 mb-4" />
            <p className="text-gray-400 text-sm">
              Canli destek icin giris yapin
            </p>
          </div>
        ) : isLoadingTicket ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4"
            >
              <Headphones className="w-8 h-8 text-white" />
            </motion.div>
            <h3 className="text-lg font-bold text-white mb-2">Merhaba!</h3>
            <p className="text-gray-400 text-sm">
              Size nasil yardimci olabiliriz?
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex flex-col max-w-[80%] ${msg.senderRole === 'user' ? 'self-start' : 'self-end'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold ${msg.senderRole === 'user' ? 'text-green-400' : 'text-indigo-400'}`}>
                    {msg.senderName}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(msg.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div
                  className={`rounded-lg px-4 py-2 ${
                    msg.senderRole === 'user'
                      ? 'bg-green-500/10 border border-green-500/20 text-gray-100 rounded-tl-sm'
                      : 'bg-indigo-500/10 border border-indigo-500/20 text-gray-100 rounded-tr-sm'
                  }`}
                >
                  <p className="text-sm">{msg.text}</p>
                </div>
              </motion.div>
            ))}
            {typingUser && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-dark-700 rounded-lg px-4 py-2">
                  <div className="flex items-center space-x-1">
                    <span className="text-gray-400 text-sm">{typingUser} yaziyor</span>
                    <div className="flex space-x-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Inline End Chat Confirmation */}
      {showEndConfirm && (
        <div className="p-4 border-t border-dark-700/50 bg-red-500/10">
          <div className="flex flex-col items-center space-y-3">
            <p className="text-sm text-gray-300 text-center">
              Gorusmeyi sonlandirmak istediginize emin misiniz?
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={async () => {
                  await closeTicket();
                  setShowEndConfirm(false);
                  onClose(); // Close the chat window
                }}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors"
              >
                Evet, Sonlandir
              </button>
              <button
                onClick={() => setShowEndConfirm(false)}
                className="px-4 py-2 bg-dark-600 hover:bg-dark-500 text-gray-300 text-sm rounded-lg transition-colors"
              >
                Iptal
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoggedIn && !showEndConfirm && (
        <div className="p-4 border-t border-dark-700/50">
          {isTicketClosed ? (
            <div className="flex flex-col items-center space-y-3">
              <div className="flex items-center space-x-2 text-gray-400">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm">Gorusme kapatildi</span>
              </div>
              <button
                onClick={handleStartNewChat}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
              >
                <MessageSquarePlus className="w-4 h-4 text-white" />
                <span className="text-white text-sm">Yeni Gorusme Baslat</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Mesajinizi yazin..."
                className="flex-1 bg-dark-700/50 border border-dark-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 text-sm"
                disabled={sending}
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                className="p-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Send className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveSupportChat;
